import {
  categoryNameSchema,
  channelNameSchema,
  channelTopicSchema,
  type AuthenticatedUser,
  type Category,
  type Channel,
  type ChannelPlacement,
  type ChannelType,
  type PublicUser,
  type UpdateChannelInput,
  type UpdateProfileInput,
} from '@discordo/shared';
import { useCallback, useMemo, useState, type ReactElement } from 'react';

import { ApiError } from '../lib/api';
import { ConfirmDialog, CreateChannelDialog, PromptDialog } from './ChannelDialogs';
import { InviteButton } from './InviteButton';
import { ProfileEditor } from './ProfileEditor';

/**
 * Left column: the guild, its channels split by type, and who is signed in.
 *
 * Text and voice are separate sections because they answer different
 * questions — which conversation am I reading, and which room am I in. The two
 * selections are independent: joining a voice channel does not move the text
 * channel being read, which is why each section has its own active id.
 *
 * A category is a group that holds channels of both kinds, so it is not inside
 * either section: it comes after both, as a collapsible block of its own.
 * Channels in no category are what the two sections show.
 *
 * Managing any of this is offered only when `manage` is given, and `manage` is
 * given only to an owner or an admin. That is a courtesy, never the rule: the
 * API refuses an ordinary member with `403` whether or not the button that
 * would have asked was ever drawn.
 */

// ---------------------------------------------------------------------------
// Collapsed categories, remembered per browser
// ---------------------------------------------------------------------------

const COLLAPSED_CATEGORIES_STORAGE_KEY = 'discordo:collapsed-categories';

/**
 * Both directions go through try/catch. A private window may refuse storage
 * outright, and a forgotten preference must never be what keeps the sidebar
 * from rendering: with no answer every category simply starts open, which is
 * the default anyway.
 */
export function readCollapsedCategories(): ReadonlySet<string> {
  try {
    const raw = window.localStorage.getItem(COLLAPSED_CATEGORIES_STORAGE_KEY);
    if (raw === null) {
      return new Set();
    }

    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return new Set();
    }
    return new Set(parsed.filter((item): item is string => typeof item === 'string'));
  } catch {
    return new Set();
  }
}

export function rememberCollapsedCategories(ids: ReadonlySet<string>): void {
  try {
    window.localStorage.setItem(COLLAPSED_CATEGORIES_STORAGE_KEY, JSON.stringify([...ids]));
  } catch {
    // Nothing to recover: this session still works, the next one just starts
    // with every category open.
  }
}

// ---------------------------------------------------------------------------
// Dragging
// ---------------------------------------------------------------------------

/**
 * The arrangement that dropping `sourceId` produces: it lands in `categoryId`,
 * immediately before `beforeId` — or at the end when there is nothing to land
 * before.
 *
 * The whole arrangement is computed, not just the moved channel, because that
 * is what the API is given: positions are indexes, so moving one channel
 * changes the position of every channel after it.
 */
export function placeChannel(
  channels: Channel[],
  sourceId: string,
  categoryId: string | null,
  beforeId: string | null,
): ChannelPlacement[] {
  const rest: ChannelPlacement[] = channels
    .filter((channel) => channel.id !== sourceId)
    .map((channel) => ({ id: channel.id, categoryId: channel.categoryId }));

  const moved: ChannelPlacement = { id: sourceId, categoryId };
  const at = beforeId === null ? -1 : rest.findIndex((entry) => entry.id === beforeId);

  if (at === -1) {
    return [...rest, moved];
  }
  return [...rest.slice(0, at), moved, ...rest.slice(at)];
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

/**
 * Everything an administrator may do to the shape of the server, as one
 * object. It travels together because it is granted together: a component that
 * receives it manages channels, and one that receives `null` does not — there
 * is no half.
 */
export interface ChannelManagement {
  createChannel: (input: {
    name: string;
    type: ChannelType;
    categoryId: string | null;
  }) => Promise<void>;
  updateChannel: (channelId: string, patch: UpdateChannelInput) => Promise<void>;
  deleteChannel: (channelId: string) => Promise<void>;
  createCategory: (name: string) => Promise<void>;
  updateCategory: (categoryId: string, name: string) => Promise<void>;
  deleteCategory: (categoryId: string) => Promise<void>;
  reorderChannels: (order: ChannelPlacement[]) => Promise<void>;
}

export interface ChannelSidebarProps {
  guildName: string;
  /** Server the sidebar is showing; the invite is cut inside it. */
  guildId: string;
  channels: Channel[];
  /** Groups of channels, in the order they are shown. */
  categories: Category[];
  activeChannelId: string | null;
  onSelectChannel: (channelId: string) => void;
  /** Voice channel this session is connected to, if any. */
  activeVoiceChannelId: string | null;
  onJoinVoice: (channelId: string) => void;
  /**
   * Who is in each voice channel, keyed by channel id. It is shown for every
   * room, joined or not — knowing where people are is what makes you decide to
   * join one.
   */
  voiceParticipants: ReadonlyMap<string, PublicUser[]>;
  user: AuthenticatedUser;
  /** Saves the nickname and the display name the person edited in the footer. */
  onUpdateProfile: (input: UpdateProfileInput) => Promise<void>;
  onLogout: () => void;
  /** Whether to offer the invite link — true for the owner and the admins. */
  canInvite: boolean;
  /** Channel and category management, or `null` for an ordinary member. */
  manage: ChannelManagement | null;
}

type DialogState =
  | { kind: 'create-channel'; type: ChannelType }
  | { kind: 'rename-channel'; channel: Channel }
  | { kind: 'topic-channel'; channel: Channel }
  | { kind: 'delete-channel'; channel: Channel }
  | { kind: 'create-category' }
  | { kind: 'rename-category'; category: Category }
  | { kind: 'delete-category'; category: Category };

const REORDER_FAILURE = 'Não foi possível salvar a nova ordem dos canais.';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ChannelSidebar({
  guildName,
  guildId,
  channels,
  categories,
  activeChannelId,
  onSelectChannel,
  activeVoiceChannelId,
  onJoinVoice,
  voiceParticipants,
  user,
  onUpdateProfile,
  onLogout,
  canInvite,
  manage,
}: ChannelSidebarProps): ReactElement {
  const [dialog, setDialog] = useState<DialogState | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(readCollapsedCategories);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loose = useMemo(
    () => channels.filter((channel) => channel.categoryId === null),
    [channels],
  );
  const textChannels = useMemo(
    () => loose.filter((channel) => channel.type === 'text'),
    [loose],
  );
  const voiceChannels = useMemo(
    () => loose.filter((channel) => channel.type === 'voice'),
    [loose],
  );

  /** Channels of each category, keyed by category id and in reading order. */
  const grouped = useMemo(() => {
    const map = new Map<string, Channel[]>();
    for (const channel of channels) {
      if (channel.categoryId === null) {
        continue;
      }
      const list = map.get(channel.categoryId);
      if (list) {
        list.push(channel);
      } else {
        map.set(channel.categoryId, [channel]);
      }
    }
    return map;
  }, [channels]);

  const toggleCategory = useCallback((categoryId: string) => {
    setCollapsed((previous) => {
      const next = new Set(previous);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      rememberCollapsedCategories(next);
      return next;
    });
  }, []);

  /**
   * Applies a drop: the dragged channel lands in `categoryId`, before
   * `beforeId`. Dropping a channel on itself is not a move and is dismissed
   * without a request.
   */
  const drop = useCallback(
    (categoryId: string | null, beforeId: string | null) => {
      const sourceId = draggingId;
      setDraggingId(null);

      if (manage === null || sourceId === null || sourceId === beforeId) {
        return;
      }

      manage
        .reorderChannels(placeChannel(channels, sourceId, categoryId, beforeId))
        .then(() => {
          setError(null);
        })
        .catch((cause: unknown) => {
          setError(cause instanceof ApiError ? cause.message : REORDER_FAILURE);
        });
    },
    [channels, draggingId, manage],
  );

  const closeDialog = useCallback(() => {
    setDialog(null);
  }, []);

  /** The three-dot menu of one channel, and the right-click that also opens it. */
  function renderChannel(channel: Channel): ReactElement {
    const voice = channel.type === 'voice';
    const selected = voice ? channel.id === activeVoiceChannelId : channel.id === activeChannelId;
    const participants = voice ? (voiceParticipants.get(channel.id) ?? []) : [];
    const menuOpen = openMenuId === channel.id;

    return (
      <li
        key={channel.id}
        className={draggingId === channel.id ? 'sidebar-row is-dragging' : 'sidebar-row'}
        draggable={manage !== null}
        onDragStart={() => {
          setDraggingId(channel.id);
        }}
        onDragEnd={() => {
          setDraggingId(null);
        }}
        onDragOver={(event) => {
          if (manage !== null) {
            // Without this the browser refuses the drop outright.
            event.preventDefault();
          }
        }}
        onDrop={(event) => {
          event.preventDefault();
          event.stopPropagation();
          drop(channel.categoryId, channel.id);
        }}
        onContextMenu={(event) => {
          if (manage === null) {
            return;
          }
          event.preventDefault();
          setOpenMenuId(channel.id);
        }}
      >
        <div className="sidebar-row-line">
          <button
            type="button"
            className={selected ? 'sidebar-channel is-active' : 'sidebar-channel'}
            // `aria-current` is only meaningful on the active entry, and the
            // sidebar renders no attribute at all on the others.
            {...(selected ? { 'aria-current': 'true' as const } : {})}
            onClick={() => {
              if (voice) {
                onJoinVoice(channel.id);
              } else {
                onSelectChannel(channel.id);
              }
            }}
          >
            <span className="sidebar-channel-sigil" aria-hidden="true">
              {voice ? '♪' : '#'}
            </span>
            {channel.name}
          </button>

          {manage !== null && (
            <div className="sidebar-menu">
              <button
                type="button"
                className="sidebar-menu-toggle"
                aria-label={`Opções de ${channel.name}`}
                aria-expanded={menuOpen}
                onClick={() => {
                  setOpenMenuId((current) => (current === channel.id ? null : channel.id));
                }}
              >
                <span aria-hidden="true">⋯</span>
              </button>

              {menuOpen && (
                <ul className="sidebar-menu-list">
                  <li>
                    <button
                      type="button"
                      onClick={() => {
                        setOpenMenuId(null);
                        setDialog({ kind: 'rename-channel', channel });
                      }}
                    >
                      Renomear
                    </button>
                  </li>
                  <li>
                    <button
                      type="button"
                      onClick={() => {
                        setOpenMenuId(null);
                        setDialog({ kind: 'topic-channel', channel });
                      }}
                    >
                      Definir tópico
                    </button>
                  </li>
                  <li>
                    <button
                      type="button"
                      className="is-destructive"
                      onClick={() => {
                        setOpenMenuId(null);
                        setDialog({ kind: 'delete-channel', channel });
                      }}
                    >
                      Apagar canal
                    </button>
                  </li>
                </ul>
              )}
            </div>
          )}
        </div>

        {participants.length > 0 && (
          <ul className="sidebar-voice-members">
            {participants.map((participant) => (
              <li key={participant.id} className="sidebar-voice-member">
                <span className="avatar is-small" aria-hidden="true">
                  {participant.displayName.slice(0, 1).toUpperCase()}
                </span>
                {participant.displayName}
              </li>
            ))}
          </ul>
        )}
      </li>
    );
  }

  function renderSection(
    title: string,
    type: ChannelType,
    list: Channel[],
    addLabel: string,
  ): ReactElement {
    return (
      <section
        className="sidebar-section"
        onDragOver={(event) => {
          if (manage !== null) {
            event.preventDefault();
          }
        }}
        onDrop={(event) => {
          event.preventDefault();
          // Dropped on the section itself, past the last row: out of every
          // category, at the end.
          drop(null, null);
        }}
      >
        <div className="sidebar-section-head">
          <h2 className="sidebar-section-title">{title}</h2>
          {manage !== null && (
            <button
              type="button"
              className="sidebar-add"
              aria-label={addLabel}
              title={addLabel}
              onClick={() => {
                setDialog({ kind: 'create-channel', type });
              }}
            >
              +
            </button>
          )}
        </div>
        <ul className="sidebar-list">{list.map(renderChannel)}</ul>
      </section>
    );
  }

  function renderCategory(category: Category): ReactElement {
    const open = !collapsed.has(category.id);
    const menuOpen = openMenuId === category.id;

    return (
      <div
        key={category.id}
        className="sidebar-category"
        onDragOver={(event) => {
          if (manage !== null) {
            event.preventDefault();
          }
        }}
        onDrop={(event) => {
          event.preventDefault();
          drop(category.id, null);
        }}
      >
        <div className="sidebar-section-head">
          <button
            type="button"
            className="sidebar-category-toggle"
            aria-expanded={open}
            onClick={() => {
              toggleCategory(category.id);
            }}
          >
            <span className="sidebar-category-arrow" aria-hidden="true">
              {open ? '▾' : '▸'}
            </span>
            {category.name}
          </button>

          {manage !== null && (
            <div className="sidebar-menu">
              <button
                type="button"
                className="sidebar-menu-toggle"
                aria-label={`Opções da categoria ${category.name}`}
                aria-expanded={menuOpen}
                onClick={() => {
                  setOpenMenuId((current) => (current === category.id ? null : category.id));
                }}
              >
                <span aria-hidden="true">⋯</span>
              </button>

              {menuOpen && (
                <ul className="sidebar-menu-list">
                  <li>
                    <button
                      type="button"
                      onClick={() => {
                        setOpenMenuId(null);
                        setDialog({ kind: 'rename-category', category });
                      }}
                    >
                      Renomear categoria
                    </button>
                  </li>
                  <li>
                    <button
                      type="button"
                      className="is-destructive"
                      onClick={() => {
                        setOpenMenuId(null);
                        setDialog({ kind: 'delete-category', category });
                      }}
                    >
                      Apagar categoria
                    </button>
                  </li>
                </ul>
              )}
            </div>
          )}
        </div>

        {open && (
          <ul className="sidebar-list">{(grouped.get(category.id) ?? []).map(renderChannel)}</ul>
        )}
      </div>
    );
  }

  function renderDialog(): ReactElement | null {
    if (dialog === null || manage === null) {
      return null;
    }

    switch (dialog.kind) {
      case 'create-channel':
        return (
          <CreateChannelDialog
            initialType={dialog.type}
            schema={channelNameSchema}
            onCreate={(input) => manage.createChannel({ ...input, categoryId: null })}
            onClose={closeDialog}
          />
        );
      case 'rename-channel':
        return (
          <PromptDialog
            title={`Renomear ${dialog.channel.name}`}
            label="Nome do canal"
            initialValue={dialog.channel.name}
            submitLabel="Salvar"
            schema={channelNameSchema}
            onSubmit={(name) => manage.updateChannel(dialog.channel.id, { name })}
            onClose={closeDialog}
          />
        );
      case 'topic-channel':
        return (
          <PromptDialog
            title={`Tópico de ${dialog.channel.name}`}
            hint="O tópico aparece no cabeçalho da conversa. Deixe em branco para tirá-lo."
            label="Tópico do canal"
            initialValue={dialog.channel.topic ?? ''}
            submitLabel="Salvar"
            schema={channelTopicSchema}
            onSubmit={(topic) =>
              manage.updateChannel(dialog.channel.id, { topic: topic === '' ? null : topic })
            }
            onClose={closeDialog}
          />
        );
      case 'delete-channel':
        return (
          <ConfirmDialog
            title={`Apagar ${dialog.channel.name}?`}
            message="As mensagens do canal são apagadas junto e não há como desfazer."
            confirmLabel="Apagar canal"
            onConfirm={() => manage.deleteChannel(dialog.channel.id)}
            onClose={closeDialog}
          />
        );
      case 'create-category':
        return (
          <PromptDialog
            title="Criar categoria"
            hint="Uma categoria agrupa canais de texto e de voz. Arraste um canal para dentro dela."
            label="Nome da categoria"
            initialValue=""
            submitLabel="Criar"
            schema={categoryNameSchema}
            onSubmit={(name) => manage.createCategory(name)}
            onClose={closeDialog}
          />
        );
      case 'rename-category':
        return (
          <PromptDialog
            title={`Renomear ${dialog.category.name}`}
            label="Nome da categoria"
            initialValue={dialog.category.name}
            submitLabel="Salvar"
            schema={categoryNameSchema}
            onSubmit={(name) => manage.updateCategory(dialog.category.id, name)}
            onClose={closeDialog}
          />
        );
      case 'delete-category':
        return (
          <ConfirmDialog
            title={`Apagar a categoria ${dialog.category.name}?`}
            message="Os canais que estão nela não são apagados: voltam para fora de qualquer categoria, com as mensagens intactas."
            confirmLabel="Apagar categoria"
            onConfirm={() => manage.deleteCategory(dialog.category.id)}
            onClose={closeDialog}
          />
        );
    }
  }

  return (
    <nav className="sidebar" aria-label="Canais">
      <header className="sidebar-header">
        <span className="sidebar-guild">{guildName}</span>
        {manage !== null && (
          <button
            type="button"
            className="sidebar-add"
            aria-label="Criar categoria"
            title="Criar categoria"
            onClick={() => {
              setDialog({ kind: 'create-category' });
            }}
          >
            +
          </button>
        )}
      </header>

      <div className="sidebar-scroll">
        {error !== null && (
          <p className="auth-alert" role="alert">
            {error}
          </p>
        )}

        {renderSection('Canais de texto', 'text', textChannels, 'Criar canal de texto')}
        {renderSection('Canais de voz', 'voice', voiceChannels, 'Criar canal de voz')}

        {categories.map(renderCategory)}

        {canInvite && (
          <section className="sidebar-section">
            <h2 className="sidebar-section-title">Convites</h2>
            <InviteButton guildId={guildId} />
          </section>
        )}
      </div>

      <footer className="sidebar-footer">
        <ProfileEditor user={user} onSave={onUpdateProfile} />
        <button type="button" className="sidebar-logout" onClick={onLogout}>
          Sair
        </button>
      </footer>

      {renderDialog()}
    </nav>
  );
}

export default ChannelSidebar;
